// ────────────────────────────────────────────────────────────────────────────
//  useShifts.ts — Hook für den Schichtplaner (Worker-Route /shifts)
// ────────────────────────────────────────────────────────────────────────────
//  Endpoints:
//    GET  /shifts                    — alle Schichten der Org (mit assignments)
//    GET  /shifts/my                 — eigene Schichten (jeder User)
//    POST /shifts                    — neue Schicht (canManageShifts)
//    PUT  /shifts/:id                — bearbeiten
//    DELETE /shifts/:id              — löschen
//    POST /shifts/:id/publish        — freigeben → System-Message
//    POST /shifts/:id/assign         — User zuweisen
//    DELETE /shifts/:id/assign/:uid  — User entfernen
//    POST /shifts/:id/respond        — accept/decline
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useState, useCallback } from 'react'
import { api } from './api'

export interface ShiftAssignment {
  id:           number
  shiftId:      number
  userId:       string
  displayName:  string
  avatarUrl:    string | null
  status:       'assigned' | 'accepted' | 'declined' | 'swapped'
  note:         string
  assignedAt:   string
  respondedAt:  string | null
}

export interface Shift {
  id:           number
  orgId:        number
  title:        string
  description:  string
  location:     string
  startsAt:     string
  endsAt:       string
  breakMinutes: number
  color:        string
  requiredRole: string
  status:       'planned' | 'published' | 'cancelled' | 'completed'
  createdBy:    string
  createdAt:    string
  updatedAt:    string
  assignments:  ShiftAssignment[]
}

export interface MyShift extends Omit<Shift, 'assignments'> {
  myStatus:     ShiftAssignment['status']
  myNote:       string
  myAssignedAt: string
  assignments?: ShiftAssignment[]
}

// ── List + Manage (Org-Owner / Admins / can_manage_shifts) ───────────────
export function useShifts(opts?: { from?: string; to?: string }) {
  const [items, setItems] = useState<Shift[]>([])
  const [canManage, setCanManage] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      const qs = new URLSearchParams()
      if (opts?.from) qs.set('from', opts.from)
      if (opts?.to)   qs.set('to',   opts.to)
      const path = '/shifts' + (qs.toString() ? `?${qs}` : '')
      const r = await api.get<{ items: Shift[]; canManageShifts: boolean }>(path)
      setItems(r.items ?? [])
      setCanManage(!!r.canManageShifts)
      setError(null)
    } catch (e: any) {
      setError(e?.message ?? 'load failed')
    } finally { setLoading(false) }
  }, [opts?.from, opts?.to])

  useEffect(() => { void refresh() }, [refresh])

  const create = async (body: Partial<Shift> & { assignedUserIds?: string[] }) => {
    await api.post('/shifts', body)
    await refresh()
  }
  const update = async (id: number, body: Partial<Shift>) => {
    await api.put(`/shifts/${id}`, body)
    await refresh()
  }
  const remove = async (id: number) => {
    await api.delete(`/shifts/${id}`)
    await refresh()
  }
  const publish = async (id: number) => {
    await api.post(`/shifts/${id}/publish`)
    await refresh()
  }
  const assign = async (id: number, userIds: string[]) => {
    await api.post(`/shifts/${id}/assign`, { userIds })
    await refresh()
  }
  const unassign = async (id: number, userId: string) => {
    await api.delete(`/shifts/${id}/assign/${userId}`)
    await refresh()
  }

  return { items, canManage, loading, error, refresh, create, update, remove, publish, assign, unassign }
}

// ── Eigene Schichten (alle User) ─────────────────────────────────────────
export function useMyShifts(opts?: { from?: string; to?: string }) {
  const [items, setItems] = useState<MyShift[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      const qs = new URLSearchParams()
      if (opts?.from) qs.set('from', opts.from)
      if (opts?.to)   qs.set('to',   opts.to)
      const path = '/shifts/my' + (qs.toString() ? `?${qs}` : '')
      const r = await api.get<{ items: MyShift[] }>(path)
      setItems(r.items ?? [])
      setError(null)
    } catch (e: any) {
      setError(e?.message ?? 'load failed')
    } finally { setLoading(false) }
  }, [opts?.from, opts?.to])

  useEffect(() => { void refresh() }, [refresh])

  const respond = async (shiftId: number, status: 'accepted' | 'declined', note?: string) => {
    await api.post(`/shifts/${shiftId}/respond`, { status, note })
    await refresh()
  }

  return { items, loading, error, refresh, respond }
}
